import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import StoreMallDirectoryRoundedIcon from "@mui/icons-material/StoreMallDirectoryRounded";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { ClientLayout } from "../components/ClientLayout";
import { clientApi, type ClientOrder } from "../services/clientApi.service";
import { canCancelOrder, getClientOrderStatusInfo } from "../utils/orderStatus";
import { buildReplayFromOrder } from "../utils/orderReplay";

/**
 * Timeline d'affichage du suivi de commande. Les libellés s'adaptent au mode (livraison
 * ou retrait sur place). Si le statut courant est `CANCELLED`, la timeline est tronquée.
 */
function getTimeline(serviceType: ClientOrder["serviceType"]): Array<{
  key: ClientOrder["status"];
  label: string;
}> {
  if (serviceType === "CATERING") {
    return [
      { key: "PENDING", label: "Reçue" },
      { key: "CONFIRMED", label: "Confirmée" },
      { key: "IN_PREPARATION", label: "En préparation" },
      { key: "READY", label: "Prête à retirer" },
      { key: "DELIVERED", label: "Récupérée" },
    ];
  }
  return [
    { key: "PENDING", label: "Reçue" },
    { key: "CONFIRMED", label: "Confirmée" },
    { key: "IN_PREPARATION", label: "En préparation" },
    { key: "READY", label: "Prête" },
    { key: "IN_DELIVERY", label: "En livraison" },
    { key: "DELIVERED", label: "Livrée" },
  ];
}

export function ClientOrderDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<ClientOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    async function load(): Promise<void> {
      setLoading(true);
      setError(null);
      try {
        const data = await clientApi.getOrder(id!);
        if (cancelled) return;
        setOrder(data);
      } catch (err) {
        if (cancelled) return;
        const message =
          (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
          "Impossible de charger cette commande.";
        setError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const isUnpaid =
    order != null &&
    !order.paymentStatus?.name?.toLowerCase().includes("payé") &&
    order.status !== "CANCELLED";

  async function handlePay(): Promise<void> {
    if (!order) return;
    setPaying(true);
    try {
      const checkout = await clientApi.createCheckout([order.id]);
      window.location.assign(checkout.url);
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
        "Impossible de démarrer le paiement.";
      setError(message);
      setPaying(false);
    }
  }

  async function handleCancel(): Promise<void> {
    if (!order) return;
    if (!window.confirm("Annuler cette commande ?")) return;
    setCancelling(true);
    try {
      const updated = await clientApi.cancelOrder(order.id);
      setOrder(updated);
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
        "Annulation impossible.";
      setError(message);
    } finally {
      setCancelling(false);
    }
  }

  return (
    <ClientLayout title="Suivi de commande">
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => navigate(-1)} aria-label="Retour">
            <ArrowBackRoundedIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Détail de la commande
          </Typography>
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}

        {loading || !order ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <Paper sx={{ p: 2 }}>
              <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                {order.serviceType === "CATERING" ? (
                  <StoreMallDirectoryRoundedIcon color="primary" />
                ) : (
                  <LocalShippingRoundedIcon color="primary" />
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {order.serviceType === "CATERING"
                      ? "Retrait sur place"
                      : "Livraison à domicile"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    N° {order.orderNumber}
                  </Typography>
                  <Chip
                    size="small"
                    sx={{ mt: 1 }}
                    color={getClientOrderStatusInfo(order.status, order.serviceType).color}
                    label={getClientOrderStatusInfo(order.status, order.serviceType).label}
                  />
                </Box>
              </Stack>
              <Divider sx={{ my: 1.5 }} />
              <Stack spacing={0.5}>
                <Typography variant="body2">
                  {format(parseISO(order.deliveryDate), "EEEE d MMMM yyyy", { locale: fr })}
                  {order.deliveryTimeSlot ? ` · ${order.deliveryTimeSlot}` : ""}
                </Typography>
                {order.serviceType === "MEAL_DELIVERY" && order.customerAddress ? (
                  <Typography variant="caption" color="text.secondary">
                    {order.customerAddress.address}
                    {order.customerAddress.city ? `, ${order.customerAddress.city}` : ""}
                  </Typography>
                ) : null}
              </Stack>
            </Paper>

            {order.status !== "CANCELLED" ? (
              <Paper sx={{ p: 2, overflow: "hidden" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Avancement
                </Typography>
                <Stepper
                  orientation="vertical"
                  activeStep={(() => {
                    const tl = getTimeline(order.serviceType);
                    const idx = tl.findIndex((t) => t.key === order.status);
                    return idx < 0 ? 0 : idx;
                  })()}
                  sx={{
                    "& .MuiStepLabel-label": {
                      typography: "body2",
                    },
                  }}
                >
                  {getTimeline(order.serviceType).map((step) => (
                    <Step key={step.key}>
                      <StepLabel>{step.label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Paper>
            ) : (
              <Alert severity="error">Cette commande a été annulée.</Alert>
            )}

            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Articles
              </Typography>
              <List dense disablePadding>
                {order.items.map((it) => (
                  <ListItem key={it.id} disablePadding sx={{ py: 0.5 }}>
                    <ListItemText
                      primary={it.product.name}
                      secondary={`${it.quantity} × ${Number(it.unitPrice).toFixed(2)} €`}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {Number(it.subtotal).toFixed(2)} €
                    </Typography>
                  </ListItem>
                ))}
              </List>
              <Divider sx={{ my: 1.5 }} />
              {order.deliveryCost != null ? (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Frais de livraison
                  </Typography>
                  <Typography variant="body2">
                    {Number(order.deliveryCost).toFixed(2)} €
                  </Typography>
                </Stack>
              ) : null}
              {order.discountAmount && order.discountAmount > 0 ? (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Réduction
                  </Typography>
                  <Typography variant="body2">
                    −{Number(order.discountAmount).toFixed(2)} €
                  </Typography>
                </Stack>
              ) : null}
              <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Total
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 700, color: "secondary.main" }}
                >
                  {Number(order.totalAmount).toFixed(2)} €
                </Typography>
              </Stack>
            </Paper>

            {order.specialInstructions ? (
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Instructions spéciales
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {order.specialInstructions}
                </Typography>
              </Paper>
            ) : null}

            {order.status === "CANCELLED" ? (
              <Button
                onClick={() =>
                  navigate("/client/order/new", {
                    state: { replay: buildReplayFromOrder(order) },
                  })
                }
                variant="contained"
                color="secondary"
                fullWidth
              >
                Repasser cette commande
              </Button>
            ) : null}

            {isUnpaid && order.status === "PENDING" ? (
              <Button
                onClick={handlePay}
                variant="contained"
                color="secondary"
                disabled={paying}
                fullWidth
              >
                {paying
                  ? "Redirection…"
                  : `Payer ${Number(order.totalAmount).toFixed(2)} €`}
              </Button>
            ) : null}

            {canCancelOrder(order.status) ? (
              <Button
                onClick={handleCancel}
                variant="outlined"
                color="error"
                disabled={cancelling}
                fullWidth
              >
                {cancelling ? "Annulation…" : "Annuler la commande"}
              </Button>
            ) : null}
          </>
        )}
      </Stack>
    </ClientLayout>
  );
}
